import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, apiError, imgUrl } from "@/lib/api";

const EMPTY = { title: "", kind: "service", description: "", items: "", published: true, image: null };

export default function AdminServices() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/services").then((r) => setItems(r.data)).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); }, []);

  const openForm = (item = null) => {
    setEditing(item);
    setForm(
      item
        ? { title: item.title, kind: item.kind || "service", description: item.description || "", items: (item.items || []).join("\n"), published: item.published, image: null }
        : EMPTY
    );
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("kind", form.kind);
      fd.append("description", form.description);
      fd.append("items", form.items);
      fd.append("published", String(form.published));
      if (form.image) fd.append("image", form.image);
      if (editing) await api.put(`/admin/services/${editing.id}`, fd);
      else await api.post("/admin/services", fd);
      toast.success("Service enregistré");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (item) => {
    try {
      const fd = new FormData();
      fd.append("title", item.title);
      fd.append("kind", item.kind || "service");
      fd.append("description", item.description || "");
      fd.append("items", (item.items || []).join("\n"));
      fd.append("published", String(!item.published));
      await api.put(`/admin/services/${item.id}`, fd);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Supprimer « ${item.title} » ?`)) return;
    await api.delete(`/admin/services/${item.id}`);
    toast.success("Service supprimé");
    load();
  };

  const move = async (index, dir) => {
    const arr = [...items];
    const j = index + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[index], arr[j]] = [arr[j], arr[index]];
    setItems(arr);
    try {
      await api.put("/admin/services-order", { ids: arr.map((x) => x.id) });
    } catch {
      load();
    }
  };

  const inputCls = "w-full rounded-xl border border-line bg-cream px-4 py-2.5 text-sm text-ink placeholder:text-muted2 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest";

  return (
    <div data-testid="admin-services">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-semibold text-2xl sm:text-3xl text-ink">Services</h1>
          <p className="mt-1 text-sm text-muted2">Les services publiés alimentent les cartes de la section « Nos services ». Type « Carte CTA » = carte sans image avec lien de contact.</p>
        </div>
        <button onClick={() => openForm()} data-testid="add-service-btn" className="inline-flex items-center gap-2 rounded-full bg-forest text-cream text-sm font-semibold px-5 py-2.5 hover:bg-pine transition-colors">
          <Plus className="h-4 w-4" /> Ajouter un service
        </button>
      </div>

      {!items ? (
        <p className="mt-10 text-sm text-muted2">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-line bg-white p-12 text-center text-sm text-muted2" data-testid="services-empty">
          Aucun service. Ajoutez votre premier service.
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {items.map((s, i) => (
            <article key={s.id} data-testid={`service-item-${s.id}`} className="rounded-2xl bg-white border border-line p-5 flex flex-wrap items-center gap-4">
              {s.img ? (
                <img src={imgUrl(s.img)} alt="" className="h-14 w-20 rounded-lg object-cover shrink-0" />
              ) : (
                <div className="h-14 w-20 rounded-lg bg-sand grid place-items-center shrink-0 text-[10px] font-semibold text-muted2">CTA</div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-terra">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-semibold text-sm text-ink">{s.title}</h3>
                  {s.kind === "cta" && <span className="rounded-full bg-sand text-body2 text-[10px] font-semibold px-2 py-0.5">Carte CTA</span>}
                </div>
                {s.description && <p className="mt-1 text-sm text-body2 line-clamp-1">{s.description}</p>}
                <p className="mt-1 text-xs text-muted2">{(s.items || []).length} prestation{(s.items || []).length > 1 ? "s" : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-xs text-body2">
                  <Switch checked={s.published} onCheckedChange={() => toggle(s)} data-testid={`service-visible-${s.id}`} />
                  {s.published ? "Visible" : "Masqué"}
                </label>
                <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Monter" className="p-1.5 rounded-lg hover:bg-sand disabled:opacity-30"><ArrowUp className="h-4 w-4 text-body2" /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Descendre" className="p-1.5 rounded-lg hover:bg-sand disabled:opacity-30"><ArrowDown className="h-4 w-4 text-body2" /></button>
                <button onClick={() => openForm(s)} aria-label="Modifier" className="p-1.5 rounded-lg hover:bg-sand" data-testid={`service-edit-${s.id}`}><Pencil className="h-4 w-4 text-body2" /></button>
                <button onClick={() => remove(s)} aria-label="Supprimer" className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`service-delete-${s.id}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="service-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifier le service" : "Nouveau service"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre du service" data-testid="service-form-title" className={inputCls} />
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="service-form-kind" className={inputCls}>
              <option value="service">Service (avec image)</option>
              <option value="cta">Carte CTA (sans image, lien contact)</option>
            </select>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Courte description (1 ligne)" data-testid="service-form-description" className={inputCls} />
            <div>
              <textarea value={form.items} onChange={(e) => setForm({ ...form, items: e.target.value })} placeholder={"Prestations, une par ligne :\nTonte\nTaille de haies\n…"} rows={5} data-testid="service-form-items" className={`${inputCls} resize-none`} />
              <p className="mt-1 text-xs text-muted2">Sur desktop, seules les 4 premières prestations sont affichées.</p>
            </div>
            {form.kind === "service" && (
              <div>
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="service-form-image-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-cream px-4 py-3 text-sm text-body2 hover:border-forest transition-colors">
                  <ImagePlus className="h-4 w-4" />
                  {form.image ? form.image.name : editing?.img ? "Remplacer l'image" : "Choisir une image"}
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="service-form-image" onChange={(e) => setForm({ ...form, image: e.target.files?.[0] || null })} />
                {editing?.img && !form.image && <img src={imgUrl(editing.img)} alt="" className="mt-2 h-20 w-32 rounded-lg object-cover" />}
              </div>
            )}
            <label className="flex items-center gap-3 text-sm text-body2">
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="service-form-visible" />
              Visible sur le site
            </label>
            <button type="submit" disabled={saving} data-testid="service-form-submit" className="w-full rounded-full bg-forest text-cream font-semibold py-3 text-sm hover:bg-pine transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Enregistrer
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
